import { useState, useEffect } from 'react';
import { Link } from 'wouter';

import { fetchCollection, productDisplayPrice, type ShopifyCollection } from '@/lib/shopifyApi';
import { Footer } from '@/components/sections/Footer';
import { useSeo } from '@/lib/useSeo';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { CollectionSeoSection } from '@/components/sections/CollectionSeoSection';
import { CollectionProductCard } from '@/components/collection/CollectionProductCard';
import { usePrerenderReady } from '@/hooks/usePrerenderReady';
import { CmsSchema, useCmsFields } from '@/cms/content';
import { configBreadcrumbs, configCollectionContext } from './categoryPageConfig';
export { FUNERAL_CONFIGS } from './funeralCollectionConfigs';
import { FUNERAL_CONFIGS } from './funeralCollectionConfigs';

/* ─── Design tokens ──────────────────────────────────────────── */
const DARK  = '#2A1F14';
const GOLD  = '#e05590';
const MUTED = 'rgba(42,31,20,0.45)';
const CREAM = '#FAF7F2';
const SLATE = '#3B4048';

/* ─── Skeleton ───────────────────────────────────────────────── */
function SkeletonGrid() {
  return (
    <div className="fcol-grid fcol-skeleton" style={{ display: 'grid', gap: 'clamp(12px,1.5vw,24px)' }}>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i}>
          <div style={{ aspectRatio: '4/5', background: 'rgba(210,60,120,0.07)' }} className="animate-pulse" />
          <div style={{ padding: '20px 22px 24px', background: '#fff' }}>
            <div style={{ height: 20, background: 'rgba(210,60,120,0.07)', marginBottom: 10, width: '70%' }} className="animate-pulse" />
            <div style={{ height: 13, background: 'rgba(210,60,120,0.07)', marginBottom: 6, width: '90%' }} className="animate-pulse" />
            <div style={{ height: 17, background: 'rgba(210,60,120,0.07)', width: '35%', marginTop: 8 }} className="animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Page ───────────────────────────────────────────────────── */
export default function FuneralCollectionPage({ handle }: { handle: string }) {
  const config = FUNERAL_CONFIGS[handle];
  const cms = useCmsFields(config ? [
    { key: 'category.heading', label: 'Nagłówek', section: 'Wstęp', kind: 'text', defaultValue: config.h1Line1, required: true },
    { key: 'category.accent', label: 'Wyróżniona część nagłówka', section: 'Wstęp', kind: 'text', defaultValue: config.h1Accent, required: true },
    { key: 'category.description', label: 'Opis kategorii', section: 'Wstęp', kind: 'textarea', defaultValue: config.description, required: true },
  ] : []);

  useSeo({
    title: config?.seoTitle ?? 'Kompozycje pogrzebowe',
    description: config?.seoDescription ?? '',
  });

  const [collection, setCollection] = useState<ShopifyCollection | null>(null);
  const [loading,    setLoading]    = useState(true);
  const [notFound,   setNotFound]   = useState(false);
  usePrerenderReady(!loading, notFound ? 404 : 200);

  useEffect(() => {
    if (!config) { setNotFound(true); setLoading(false); return; }
    setLoading(true);
    setNotFound(false);
    fetchCollection(config.handle, { sort: 'COLLECTION_DEFAULT', reverse: false, limit: 100 })
      .then(col => { if (!col) setNotFound(true); else setCollection(col); })
      .catch(err => {
        // Only a real null/404 response is a missing collection.
        console.warn(`[FuneralCollectionPage] refresh failed for ${config.handle}:`, err);
      })
      .finally(() => setLoading(false));
  }, [handle]);

  if (!config) {
    return (
      <>
        <PageBreadcrumb crumbs={[
          { label: 'Kompozycje pogrzebowe', href: '/kompozycje-pogrzebowe/' },
          { label: 'Nie znaleziono kolekcji' },
        ]} />
        <div style={{ background: '#fff', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, color: DARK, marginBottom: 16 }}>
              Nie znaleziono kolekcji
            </p>
            <Link href="/kompozycje-pogrzebowe/" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, letterSpacing: '0.1em', color: GOLD, textDecoration: 'none', textTransform: 'uppercase' }}>
              ← Kompozycje pogrzebowe
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const allProducts = (collection?.products.nodes ?? []).map(p => ({ ...p, tags: p.tags ?? [] }));

  return (
    <>
      <CmsSchema fields={cms.fields} />
      <div style={{ background: '#fff', minHeight: '100vh' }}>

        {/* ── Hero ─────────────────────────────────────────────── */}
        <section style={{
          background: '#fff',
          position: 'relative', overflow: 'hidden',
          minHeight: 'calc(52dvh - 45px)', display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}>
          <PageBreadcrumb crumbs={configBreadcrumbs(config)} />

          <div className="page-container" style={{
            paddingTop: 'max(clamp(24px,3vw,44px), calc(var(--page-breadcrumb-height, 44px) + 12px))',
            paddingBottom: 'clamp(20px,2.5vw,32px)',
          }}>
            <h1
              className="bk-fade-up bk-d1"
              style={{
                fontFamily: 'Cormorant Garamond, Georgia, serif',
                fontSize: 'clamp(42px,6vw,76px)', fontWeight: 600,
                lineHeight: 1.08, color: DARK, margin: '0 0 20px', letterSpacing: '-0.01em',
              }}
            >
              <span {...cms.text('category.heading')}>{cms.value('category.heading')}</span><br />
              <span {...cms.text('category.accent')} style={{ color: SLATE }}>{cms.value('category.accent')}</span>
            </h1>

            <p
              className="bk-fade-up bk-d2"
              style={{
                fontFamily: 'DM Sans, sans-serif', fontSize: 16, lineHeight: 1.7,
                color: MUTED, maxWidth: 440, marginBottom: 32,
              }}
            >
              <span {...cms.text('category.description')}>{cms.value('category.description')}</span>
            </p>

            {!loading && !notFound && (
              <p className="bk-fade-in" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, color: MUTED }}>
                {allProducts.length} {allProducts.length === 1 ? 'kompozycja' : allProducts.length < 5 ? 'kompozycje' : 'kompozycji'} w ofercie
              </p>
            )}
          </div>
        </section>

        {/* ── Products ─────────────────────────────────────────── */}
        <div className="page-container" style={{ background: '#ffffff' }}>
        <div style={{
          padding: 'clamp(40px,5vw,72px) 0 clamp(64px,8vw,120px)',
        }}>
          <style>{`
            .fcol-grid { grid-template-columns: repeat(4, 1fr); }
            @media (max-width: 768px)  { .fcol-grid { grid-template-columns: repeat(2, 1fr) !important; } }
          `}</style>

          {loading && <SkeletonGrid />}

          {!loading && notFound && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 30, color: DARK, marginBottom: 16 }}>
                Kolekcja niedostępna
              </p>
              <Link
                href="/kompozycje-pogrzebowe/"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, letterSpacing: '0.1em', color: GOLD, textDecoration: 'none', textTransform: 'uppercase' }}
              >
                ← Kompozycje pogrzebowe
              </Link>
            </div>
          )}

          {!loading && !notFound && allProducts.length === 0 && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, color: DARK }}>
                Brak produktów w tej kategorii
              </p>
            </div>
          )}

          {!loading && allProducts.length > 0 && (
            <div className="fcol-grid" style={{ display: 'grid', gap: 'clamp(12px,1.5vw,24px)' }}>
              {allProducts.map((product, i) => (
                <CollectionProductCard
                  key={product.handle}
                  product={product}
                  price={productDisplayPrice(product.priceRange)}
                  index={i}
                  collectionCtx={configCollectionContext(config, `/${config.handle}`)}
                  centerInfo
                />
              ))}
            </div>
          )}
        </div>
        </div>{/* end white section */}

        {collection && <CollectionSeoSection handle={config.handle} />}
      </div>
      <Footer />
    </>
  );
}
